"use client";

import NoImageSrc from "@/assets/images/no_image.svg";
import { CLink } from "@/components/common/customLink/CustomLink";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/tailwind";
import CldImage from "@/components/ui/cldImg/CldImage";
import type { variantData } from "./ProductColorVariants";
import { useEffect, useState } from "react";

interface PairEyewearSwatchesProps {
  variantsData: variantData[];
  selectedvariantId: string;
}

export default function PairEyewearSwatches({
  variantsData,
  selectedvariantId,
}: PairEyewearSwatchesProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const count = 4;
  const selectedVariantIdx = variantsData?.findIndex(
    (a) => a?.variantId === selectedvariantId
  );

  const [viewAll, setViewAll] = useState<boolean>(
    selectedVariantIdx + 1 > count || isMobile ? true : false
  );
  const [swatchVariants, setSwatchVariants] = useState(
    variantsData?.slice(0, count)
  );

  const handleClick = () => {
    setViewAll(!viewAll);
  };

  useEffect(() => {
    viewAll
      ? setSwatchVariants(variantsData)
      : setSwatchVariants(variantsData?.slice(0, count));
  }, [viewAll]);

  return (
    <div className="w-full">
      <div className="no-scrollbar my-[10px] flex w-[90vw] gap-3 max-md:overflow-x-scroll md:my-3 md:w-full md:flex-wrap">
        {swatchVariants?.map((item, index) => {
          const isSelected = selectedvariantId === item.variantId;
          return (
            <CLink
              href={item.landingPageBaseUrl}
              key={"swatch" + index}
              className={cn(
                "relative flex min-h-[4.5rem] min-w-[4.5rem] cursor-pointer items-center justify-center border border-white bg-tertiary-5 transition-all duration-200 ease-in-out hover:border hover:border-black lg:min-h-24 lg:min-w-24",
                isSelected ? "border-black" : "",
                item.quantityAvailable <= 0 ? "opacity-40" : "",
              )}
              title={item.cliponFrameColor || item.frameColor || item.color}
            >
              {item.pairSwatchImage ? (
                <CldImage
                  src={item.pairSwatchImage}
                  alt={item.cliponFrameColor || item.frameColor || item.color || ""}
                  width={64}
                  height={64}
                  className="w-[3.875rem] invert-[2%] md:w-[4.25rem] rounded-full"
                />
              ) : item.hexcode ? (
                <div
                  className="h-[3.875rem] w-[3.875rem] rounded-full md:h-[4.25rem] md:w-[4.25rem]"
                  style={{ backgroundColor: item.hexcode }}
                />
              ) : (
                <CldImage
                  src={NoImageSrc}
                  alt={item.cliponFrameColor || item.frameColor || item.color || ""}
                  width={64}
                  height={64}
                  className="w-[3.875rem] invert-[2%] md:w-[4.25rem] rounded-full"
                />
              )}
              {item.quantityAvailable <= 0 && (
                <div className="absolute top-0 min-h-full min-w-[2px] rotate-45 bg-tertiary-6"></div>
              )}
            </CLink>
          );
        })}
        {variantsData?.length > count && (
          <div
            className={cn(
              "min-h-20 min-w-20 cursor-pointer items-center justify-center border-black bg-tertiary-5 hover:border md:flex lg:min-h-24 lg:min-w-24",
              viewAll ? "hidden" : "flex"
            )}
            onClick={() => handleClick()}
          >
            {viewAll ? (
              <p className="px-1 text-center font-semibold">
                Show <br /> Less
              </p>
            ) : (
              <p className="text-xl font-semibold">
                +{variantsData?.length - count}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
